export type MerchantConnectionState =
  | "not_configured"
  | "connecting"
  | "connected"
  | "connection_failed"
  | "reauthorization_required";

export const MERCHANT_CONNECTION_LABELS: Record<MerchantConnectionState, string> = {
  not_configured: "Not configured",
  connecting: "Connecting",
  connected: "Connected",
  connection_failed: "Connection failed",
  reauthorization_required: "Reauthorization required",
};

const ACTIVE_CONNECT_MS = 2 * 60 * 1000;

/** A connect attempt is live only while Tradivo has a recent in-progress timestamp. */
export function connectionAttemptIsLive(
  lastAttemptAt: string | null | undefined,
  now = Date.now(),
): boolean {
  if (!lastAttemptAt) return false;
  const ms = Date.parse(lastAttemptAt);
  if (Number.isNaN(ms)) return false;
  const age = now - ms;
  return age >= 0 && age <= ACTIVE_CONNECT_MS;
}

export function deriveMerchantConnectionState(
  authStatus: string | null | undefined,
  healthStatus: string | null | undefined,
): MerchantConnectionState {
  const auth = (authStatus ?? "").trim();
  const health = (healthStatus ?? "").trim();
  if (auth === "expired" || auth === "revoked") return "reauthorization_required";
  if (auth === "error" || auth === "degraded" || health === "error") return "connection_failed";
  if (auth === "connecting" || auth === "pending" || auth === "configured") return "connecting";
  if (auth === "authorized" || auth === "connected") return "connected";
  return "not_configured";
}

export function merchantConnectionLabel(
  authStatus: string | null | undefined,
  healthStatus: string | null | undefined,
  opts?: { attemptLive?: boolean },
): string {
  const auth = (authStatus ?? "").trim();
  const staleAttempt = auth === "configured" || ((auth === "connecting" || auth === "pending") && !opts?.attemptLive);
  if (staleAttempt) return "Authentication required";
  return MERCHANT_CONNECTION_LABELS[deriveMerchantConnectionState(authStatus, healthStatus)];
}

export type MerchantConnectionAction = "connect" | "test" | "reconnect" | "disconnect";

export function merchantConnectionActions(connector: {
  auth_status: string | null | undefined;
  health_status: string | null | undefined;
}): MerchantConnectionAction[] {
  const state = deriveMerchantConnectionState(connector.auth_status, connector.health_status);
  switch (state) {
    case "not_configured":
      return ["connect", "disconnect"];
    case "connecting":
      return ["connect", "disconnect"];
    case "connected":
      return ["test", "reconnect", "disconnect"];
    case "connection_failed":
      return ["reconnect", "test", "disconnect"];
    case "reauthorization_required":
      return ["reconnect", "disconnect"];
  }
}
